import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "motion/react";

const DPR = 2; // canvas resolution multiplier (keeps the particles crisp)

const PlaceholdersAndVanishInput = forwardRef(
  function PlaceholdersAndVanishInput(
    {
      placeholders = [],
      value,
      onChange,
      onFocus,
      onKeyDown,
      onVanished, // called when the vanish animation ends (optional)
      inputStyle,
      children, // e.g. a loading spinner, rendered inside the input wrapper
    },
    ref
  ) {
    const [current, setCurrent] = useState(0);
    const [animating, setAnimating] = useState(false);

    const inputRef = useRef(null);
    const canvasRef = useRef(null);
    const particlesRef = useRef([]);
    const rafRef = useRef(null);
    const animatingRef = useRef(false);

    /* ---------- sliding placeholders ---------- */
    useEffect(() => {
      if (placeholders.length < 2) return undefined;
      let id = null;
      const stop = () => {
        if (id) {
          clearInterval(id);
          id = null;
        }
      };
      const start = () => {
        stop();
        id = setInterval(
          () => setCurrent((p) => (p + 1) % placeholders.length),
          3000
        );
      };
      const onVisibility = () =>
        document.visibilityState === "visible" ? start() : stop();

      start();
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        stop();
        document.removeEventListener("visibilitychange", onVisibility);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeholders.length]);

    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    /* ---------- vanish effect ---------- */
    // Draws the current text onto the canvas and turns its pixels into particles.
    const buildParticles = () => {
      const input = inputRef.current;
      const canvas = canvasRef.current;
      if (!input || !canvas) return 0;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      const w = input.offsetWidth * DPR;
      const h = input.offsetHeight * DPR;
      canvas.width = w;
      canvas.height = h;
      ctx.clearRect(0, 0, w, h);

      const cs = getComputedStyle(input);
      ctx.font = `${cs.fontWeight} ${parseFloat(cs.fontSize) * DPR}px ${
        cs.fontFamily
      }`;
      ctx.fillStyle = cs.color;
      ctx.textBaseline = "middle";
      const startX =
        (parseFloat(cs.paddingLeft) + parseFloat(cs.borderLeftWidth)) * DPR;
      ctx.fillText(input.value, startX, h / 2);

      const { data } = ctx.getImageData(0, 0, w, h);
      const particles = [];
      let maxX = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          if (data[i + 3] !== 0) {
            particles.push({
              x,
              y,
              r: 1,
              color: `rgba(${data[i]}, ${data[i + 1]}, ${data[i + 2]}, ${
                data[i + 3] / 255
              })`,
            });
            if (x > maxX) maxX = x;
          }
        }
      }
      particlesRef.current = particles;
      return maxX;
    };

    const finish = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
      }
      particlesRef.current = [];
      animatingRef.current = false;
      setAnimating(false);
      onVanished && onVanished();
    };

    const animate = (startX) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        finish();
        return;
      }
      const ctx = canvas.getContext("2d");

      const step = (pos) => {
        rafRef.current = requestAnimationFrame(() => {
          const next = [];
          for (const p of particlesRef.current) {
            if (p.x < pos) {
              next.push(p);
              continue;
            }
            if (p.r <= 0) continue;
            p.x += Math.random() > 0.5 ? 1 : -1;
            p.y += Math.random() > 0.5 ? 1 : -1;
            p.r -= 0.05 * Math.random();
            next.push(p);
          }
          particlesRef.current = next;

          ctx.clearRect(pos, 0, canvas.width, canvas.height);
          for (const p of next) {
            if (p.x > pos) {
              ctx.beginPath();
              ctx.rect(p.x, p.y, p.r, p.r);
              ctx.fillStyle = p.color;
              ctx.fill();
            }
          }

          if (next.length > 0) step(pos - 8);
          else finish();
        });
      };
      step(startX);
    };

    const vanish = () => {
      const input = inputRef.current;
      if (animatingRef.current || !input || !input.value) return;
      const maxX = buildParticles(); // must run BEFORE the text goes transparent
      animatingRef.current = true;
      setAnimating(true);
      animate(maxX);
    };

    // lets the parent call  vanishRef.current.vanish()
    useImperativeHandle(ref, () => ({
      vanish,
      focus: () => inputRef.current && inputRef.current.focus(),
    }));

    return (
      <div
        style={{
          position: "relative",
          flex: 1,
          display: "flex",
          alignItems: "stretch",
          boxSizing: "border-box",
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={value ?? ""}
          onChange={(e) => {
            if (!animatingRef.current) onChange && onChange(e);
          }}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          style={{
            boxSizing: "border-box",
            width: "100%",
            height: "44px",
            padding: "0 44px 0 14px",
            border: "1px solid #E2E8F0",
            borderRadius: 0,
            fontSize: "14px",
            background: "#FFFFFF",
            ...inputStyle,
            color: animating ? "transparent" : "#0F172A",
            caretColor: animating ? "transparent" : undefined,
          }}
        />

        {/* particles */}
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            opacity: animating ? 1 : 0,
          }}
        />

        {/* sliding placeholder */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            paddingLeft: "15px",
            pointerEvents: "none",
          }}
        >
          <AnimatePresence mode="wait">
            {!value && placeholders.length > 0 && (
              <motion.p
                key={`placeholder-${current}`}
                initial={{ y: 5, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -15, opacity: 0 }}
                transition={{ duration: 0.3, ease: "linear" }}
                style={{
                  margin: 0,
                  width: "100%",
                  paddingRight: "44px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  color: "#94A3B8",
                }}
              >
                {placeholders[current]}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {children}
      </div>
    );
  }
);

export default PlaceholdersAndVanishInput;